import React from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Stack,
  Typography,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import EmailSendDetailDialog from '../../emailAlerts/EmailSendDetailDialog';
import SendHistoryResponseModal from '../../SendHistoryResponseModal';

const getNestedValue = (root, source) => {
  if (!root || !source) return root;
  const segments = String(source)
    .split('.')
    .map((segment) => segment.trim())
    .filter(Boolean);
  return segments.reduce((acc, segment) => (acc == null ? acc : acc[segment]), root);
};

const resolveSource = ({ source, metadata, definition, row }) => {
  if (!source || source === 'definition') return definition;
  if (source === 'metadata') return metadata;
  if (source === 'row') return row;
  if (source.startsWith('row.')) return getNestedValue(row, source.replace(/^row\./, ''));
  if (source.startsWith('metadata.')) return getNestedValue(metadata, source.replace(/^metadata\./, ''));
  if (source.startsWith('definition.')) return getNestedValue(definition, source.replace(/^definition\./, ''));
  return definition;
};

const buildSendHistoryEntry = (row) => {
  if (!row) return null;
  return {
    status: row.sendStatus ?? row.status,
    statusCode: row.statusCode ?? null,
    durationMs: row.durationMs ?? null,
    createdAt: row.createdAt ?? row.sendTimestamp ?? null,
    responseBody: row.responseBody ?? null,
    errorMessage: row.errorMessage ?? null,
    payload: Array.isArray(row.payload) ? row.payload : [row],
  };
};

const normalizeTableRows = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    if (Array.isArray(value.rows)) return value.rows;
    if (Array.isArray(value.items)) return value.items;
  }
  if (typeof value === 'string' && value.trim().length) {
    try {
      const parsed = JSON.parse(value);
      return normalizeTableRows(parsed);
    } catch (_error) {
      return [];
    }
  }
  return [];
};

const renderSectionValue = (value, format) => {
  if (format === 'summary') {
    if (!value || typeof value !== 'object') {
      return (
        <Typography variant="body2" color="text.secondary">
          {value == null ? '—' : String(value)}
        </Typography>
      );
    }
    const excluded = new Set(['dpeItems', 'appsheetItems', '__rowId', '__rowKey']);
    const entries = Object.entries(value).filter(([key, entryValue]) => {
      if (excluded.has(key)) return false;
      if (entryValue == null) return false;
      if (Array.isArray(entryValue)) return false;
      if (typeof entryValue === 'object') return false;
      return true;
    });
    if (!entries.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No summary fields available.
        </Typography>
      );
    }
    const formatLabel = (label) =>
      String(label || '')
        .replace(/[_.]/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (letter) => letter.toUpperCase());
    const formatValue = (label, entryValue) => {
      if (entryValue == null) return '—';
      const normalizedLabel = String(label || '').toLowerCase();
      if (normalizedLabel.includes('mrr') || normalizedLabel.includes('delta')) {
        const numeric = Number(entryValue);
        if (!Number.isFinite(numeric)) return String(entryValue);
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          maximumFractionDigits: 2,
        }).format(numeric);
      }
      if (normalizedLabel.includes('count')) {
        const numeric = Number(entryValue);
        if (!Number.isFinite(numeric)) return String(entryValue);
        return new Intl.NumberFormat('en-US').format(numeric);
      }
      return String(entryValue);
    };
    return (
      <Stack spacing={1}>
        {entries.map(([key, entryValue]) => (
          <Stack
            key={key}
            direction="row"
            spacing={2}
            alignItems="baseline"
            sx={{ justifyContent: 'space-between' }}
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {formatLabel(key)}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'right' }}>
              {formatValue(key, entryValue)}
            </Typography>
          </Stack>
        ))}
      </Stack>
    );
  }
  if (format === 'table') {
    const rows = normalizeTableRows(value);
    if (!rows.length) {
      return (
        <Typography variant="body2" color="text.secondary">
          No rows available.
        </Typography>
      );
    }
    const columnSet = new Set();
    rows.forEach((row) => {
      if (!row || typeof row !== 'object') return;
      Object.keys(row).forEach((key) => columnSet.add(key));
    });
    const columns = Array.from(columnSet).map((key) => ({
      field: key,
      headerName: String(key || '')
        .replace(/[_.]/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, (letter) => letter.toUpperCase()),
      flex: 1,
      minWidth: 140,
      valueGetter: (_value, row) => {
        const entryValue = row?.[key];
        if (entryValue == null) return '—';
        if (typeof entryValue === 'object') return JSON.stringify(entryValue);
        return entryValue;
      },
    }));
    const safeRows = rows.map((row, index) => ({
      id: row?.orderItemId || row?.selectionId || row?.id || `row-${index}`,
      ...row,
    }));
    return (
      <div style={{ width: '100%' }}>
        <DataGrid
          autoHeight
          rows={safeRows}
          columns={columns}
          getRowId={(entry) => entry?.id || entry?.orderItemId || entry?.selectionId || entry?.rowId}
          disableRowSelectionOnClick
          hideFooter
          density="compact"
        />
      </div>
    );
  }
  if (format === 'text') {
    return (
      <Typography variant="body2" color="text.secondary">
        {value == null ? '—' : String(value)}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" color="text.secondary">
      {JSON.stringify(value, null, 2)}
    </Typography>
  );
};

const ReportModals = ({ activeModalId, modals, metadata, definition, row, onClose, onAction, actionState }) => {
  if (!activeModalId) return null;
  const modal = modals.find((item) => item.id === activeModalId);
  if (!modal) return null;

  const title = modal.params?.title || 'Details';

  const renderBody = () => {
    if (modal.type === 'json_viewer') {
      const payload = resolveSource({ source: modal.params?.source, metadata, definition, row });
      return (
        <Typography component="pre" variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {JSON.stringify(payload, null, 2)}
        </Typography>
      );
    }

    if (modal.type === 'detail_panel') {
      const sections = Array.isArray(modal.params?.sections) ? modal.params.sections : [];
      if (!sections.length) {
        return <Typography variant="body2">No sections configured.</Typography>;
      }
      return (
        <Stack spacing={2}>
          {sections.map((section, index) => {
            const content = renderSectionValue(
              resolveSource({ source: section.source, metadata, definition, row }),
              section.format || 'json'
            );
            if (section.format === 'summary') {
              return (
                <Stack key={`${section.label}-${index}`} spacing={0.5}>
                  <Accordion defaultExpanded={false} disableGutters elevation={0}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="subtitle2">{section.label}</Typography>
                    </AccordionSummary>
                    <AccordionDetails>{content}</AccordionDetails>
                  </Accordion>
                  {index < sections.length - 1 && <Divider />}
                </Stack>
              );
            }

            return (
              <Stack key={`${section.label}-${index}`} spacing={0.5}>
                <Typography variant="subtitle2">{section.label}</Typography>
                {content}
                {index < sections.length - 1 && <Divider />}
              </Stack>
            );
          })}
        </Stack>
      );
    }

    if (modal.type === 'confirm_dialog') {
      return (
        <Typography variant="body2">
          {modal.params?.message || 'Confirm this action.'}
        </Typography>
      );
    }

    return (
      <Typography variant="body2" color="text.secondary">
        Unsupported modal type: {modal.type}
      </Typography>
    );
  };

  if (modal.type === 'email_send_detail') {
    const detail = row || null;
    if (!detail) return null;
    return (
      <EmailSendDetailDialog
        open
        detail={detail}
        loading={false}
        onClose={onClose}
        onDelete={() => onAction?.({ type: 'email_send_delete', params: { label: 'Delete' } }, row)}
        onRetry={() => onAction?.({ type: 'email_send_retry', params: { label: 'Retry' } }, row)}
        retrying={Boolean(actionState?.emailRetryPending)}
        showDelete={
          Boolean(modal.params?.allowDelete) &&
          detail.genericActions?.delete !== false
        }
        showRetry={
          Boolean(modal.params?.allowRetry) &&
          detail.genericActions?.retry !== false
        }
      />
    );
  }

  if (modal.type === 'send_history_response') {
    const entry = buildSendHistoryEntry(row);
    if (!entry) return null;
    return (
      <SendHistoryResponseModal
        open
        onClose={onClose}
        entry={entry}
        record={row}
      />
    );
  }

  const closeLabel = modal.type === 'json_viewer' ? 'Close details modal' : 'Close';

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{title}</DialogTitle>
      <DialogContent dividers>{renderBody()}</DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{closeLabel}</Button>
        {modal.type === 'confirm_dialog' && (
          <Button variant="contained" onClick={onClose}>
            {modal.params?.confirmLabel || 'Confirm'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportModals;
